import {Scroller, ScrollerButton, ScrollerProvider} from '@graphcommerce/framer-scroller'
import {IconSvg, IconSvgProps} from '@graphcommerce/next-ui/IconSvg'
import {iconChevronLeft, iconChevronRight} from '@graphcommerce/next-ui/icons'
import React from 'react'
import {Box, BoxProps} from '@mui/material'
import {extendableComponent} from '@graphcommerce/next-ui/Styles/extendableComponent'
import {ProductListLink} from '@graphcommerce/magento-product'
import {useRouter} from 'next/router'

export type SubcategoryNavigationProps = {
  parentCategory: any
  category: any,
  params: any,
  iconLeft?: IconSvgProps['src'],
  iconRight?: IconSvgProps['src'],
  linkPrefix?: string
} & Pick<BoxProps, 'sx'>

const { classes, selectors } = extendableComponent('SubCategoryNavigation', [
  'root',
  'scroller',
  'button',
  'left',
  'right',
  'link'
] as const)

export function SubCategoryNavigation(props: SubcategoryNavigationProps) {
  const { parentCategory, category, params, iconLeft, iconRight, linkPrefix, sx = [] } = props
  const children = category.children.length > 0 ? category.children : parentCategory?.children
  const path = useRouter().asPath.split('?')[0].slice(1)

  return (
    <ScrollerProvider scrollSnapAlign='none'>
      <Box
        className={classes.root}
        sx={[
          {
            width: '100%',
            display: { xs: 'grid', md: 'grid' },
            alignItems: 'center',
            position: 'relative',
            pointerEvents: 'all',
            gridTemplateColumns: `auto 1fr auto`,
            justifyContent: 'flex-start',
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        <Scroller
          hideScrollbar
          className={classes.scroller}
        >
          {children?.map((cat) => {
            if (!cat?.url_path || !cat.name || cat.include_in_menu !== 1) return null

            const selected = cat.url_path === path
            const itemLinkPrefix = cat.linkPrefix ?? linkPrefix
            return (
              <ProductListLink
                key={cat.url_path}
                underline='none'
                color='inherit'
                url={itemLinkPrefix ? `${itemLinkPrefix}/${cat.url_path}` : cat.url_path}
                filters={{ category_uid: { eq: cat.uid } }}
                sort={params.sort}
                className={classes.link}
                sx={(theme) => ({
                  whiteSpace: 'nowrap',
                  display: 'block',
                  marginRight: `${theme.spacings.xxs}`,
                  marginLeft: `${theme.spacings.xxs}`,
                  typography: 'h6',
                  position: 'relative',
                  marginBottom: 0,
                  paddingBottom: 0,
                  textAlign: 'center',
                  '&:before': {
                    content: '""',
                    width: 40,
                    height: 2,
                    background: theme.palette.primary.main,
                    position: 'absolute',
                    left: 0,
                    right: 0,
                    margin: '0 auto',
                    transition: 'opacity .2s ease, bottom .2s ease',
                    bottom: 0,
                    opacity: (selected ? 1 : 0),
                  },
                  '&:hover': {
                    '&:before': {
                      opacity: 1,
                      bottom: 0,
                    },
                  }
                })}
              >
                {cat.name}
              </ProductListLink>
            )
          })}
        </Scroller>

        <ScrollerButton
          sxContainer={{
            gridArea: `1 / 1 / 1 / 2`,
            pointerEvents: 'none',
            '& > *': { pointerEvents: 'all' },
          }}
          sx={{
            pointerEvents: 'all',
            boxShadow: 'none',
            height: 48,
            borderTopLeftRadius: 0,
            borderBottomLeftRadius: 0,
            backgroundColor: 'transparent',
            display: { xs: 'flex', md: 'flex' },
            backgroundImage: (theme) =>
              `linear-gradient(to left, rgba(255,255,255,0) 0%, ${theme.palette.background.default} 35%)`,
          }}
          direction='left'
          size='small'
          className={`${classes.left} ${classes.button}`}
        >
          <IconSvg src={iconLeft ?? iconChevronLeft} />
        </ScrollerButton>

        <ScrollerButton
          sxContainer={{
            gridArea: `1 / 3 / 1 / 4`,
            pointerEvents: 'none',
            '& > *': {
              pointerEvents: 'all',
            },
          }}
          sx={{
            pointerEvents: 'all',
            boxShadow: 'none',
            height: 48,
            borderTopRightRadius: 0,
            borderBottomRightRadius: 0,
            backgroundColor: 'transparent',
            display: { xs: 'flex', md: 'flex' },
            backgroundImage: (theme) =>
              `linear-gradient(to right, rgba(255,255,255,0) 0%, ${theme.palette.background.default} 35%)`,
          }}
          direction='right'
          size='small'
          className={`${classes.right} ${classes.button}`}
        >
          <IconSvg src={iconRight ?? iconChevronRight} />
        </ScrollerButton>
      </Box>
    </ScrollerProvider>
  )
}
